/**
 * StrategyTemplatePicker - Template selection for strategy creation
 *
 * Shows 4 strategy templates + blank notebook option.
 * Used in ComputePromptModal after GPU selection.
 */

import { FiTrendingUp, FiActivity, FiCpu, FiZap, FiFile, FiCheck } from 'react-icons/fi';
import { HiSparkles } from 'react-icons/hi';

export interface StrategyTemplate {
  id: string;
  name: string;
  path: string;
  description: string;
  skillLevel: 'Beginner' | 'Intermediate' | 'Expert';
  icon: 'momentum' | 'mean-reversion' | 'ml' | 'deep-learning' | 'crypto' | 'blank';
  tags?: string[];
  gpuOptimized?: boolean;
  /** When true, the sample is a multi-file directory project */
  isDirectory?: boolean;
  /** Path to the directory containing companion files (e.g. .py modules) */
  directoryPath?: string;
}

export const STRATEGY_TEMPLATES: StrategyTemplate[] = [
  {
    id: 'momentum',
    name: 'Momentum Strategy',
    path: 'samples/strategy_templates/Strategy_01_Momentum.ipynb',
    description: 'RSI + Moving Average crossover',
    skillLevel: 'Beginner',
    icon: 'momentum',
    tags: ['popular']
  },
  {
    id: 'crypto-rsi',
    name: 'Crypto RSI Scanner',
    path: 'samples/crypto_rsi_scanner/01_rsi_scanner_pandas.ipynb',
    description: 'Multi-timeframe RSI scanner across 200+ crypto pairs with interactive live dashboard',
    skillLevel: 'Beginner',
    icon: 'crypto',
    tags: ['featured', 'crypto'],
    gpuOptimized: true,
    isDirectory: true,
    directoryPath: 'samples/crypto_rsi_scanner',
  },
  {
    id: 'mean-reversion',
    name: 'Mean Reversion',
    path: 'samples/strategy_templates/Strategy_02_Mean_Reversion.ipynb',
    description: 'Bollinger Bands strategy',
    skillLevel: 'Intermediate',
    icon: 'mean-reversion',
    tags: ['statistical']
  },
  {
    id: 'ml-classifier',
    name: 'ML Classifier',
    path: 'samples/strategy_templates/Strategy_03_ML_Classifier.ipynb',
    description: 'GPU-accelerated ML with cuML',
    skillLevel: 'Intermediate',
    icon: 'ml',
    tags: ['machine-learning'],
    gpuOptimized: true
  },
  {
    id: 'deep-learning',
    name: 'Deep Learning',
    path: 'samples/strategy_templates/Strategy_04_Deep_Learning.ipynb',
    description: 'GPU-accelerated XGBoost with RAPIDS',
    skillLevel: 'Expert',
    icon: 'deep-learning',
    tags: ['gpu-required'],
    gpuOptimized: true
  },
  {
    id: 'blank',
    name: 'Blank Notebook',
    path: '',
    description: 'Start from scratch',
    skillLevel: 'Beginner',
    icon: 'blank'
  }
];

interface StrategyTemplatePickerProps {
  selectedTemplate: string | null;
  onSelect: (templateId: string, templatePath: string) => void;
  compact?: boolean;
}

export default function StrategyTemplatePicker({
  selectedTemplate,
  onSelect,
  compact = false
}: StrategyTemplatePickerProps) {
  const getIcon = (iconType: StrategyTemplate['icon']) => {
    switch (iconType) {
      case 'momentum':
        return <FiTrendingUp className="w-5 h-5" />;
      case 'mean-reversion':
        return <FiActivity className="w-5 h-5" />;
      case 'ml':
        return <FiCpu className="w-5 h-5" />;
      case 'deep-learning':
        return <FiZap className="w-5 h-5" />;
      case 'crypto':
        return <FiActivity className="w-5 h-5" />;
      case 'blank':
        return <FiFile className="w-5 h-5" />;
      default:
        return <FiFile className="w-5 h-5" />;
    }
  };

  const getSkillColor = (level: StrategyTemplate['skillLevel']) => {
    switch (level) {
      case 'Beginner':
        return 'bg-green-500/20 text-green-400';
      case 'Intermediate':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'Expert':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getIconColor = (iconType: StrategyTemplate['icon']) => {
    switch (iconType) {
      case 'momentum':
        return 'text-green-400 bg-green-400/10';
      case 'mean-reversion':
        return 'text-blue-400 bg-blue-400/10';
      case 'ml':
        return 'text-purple-400 bg-purple-400/10';
      case 'deep-learning':
        return 'text-orange-400 bg-orange-400/10';
      case 'crypto':
        return 'text-cyan-400 bg-cyan-400/10';
      case 'blank':
        return 'text-gray-400 bg-gray-400/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  if (compact) {
    return (
      <div className="grid grid-cols-2 gap-2">
        {STRATEGY_TEMPLATES.map((template) => (
          <button
            key={template.id}
            onClick={() => onSelect(template.id, template.path)}
            className={`p-3 rounded-lg border text-left transition-all ${
              selectedTemplate === template.id
                ? 'border-profit bg-profit/10'
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className={`p-1.5 rounded ${getIconColor(template.icon)}`}>
                {getIcon(template.icon)}
              </div>
              <span className="text-sm font-medium text-white truncate">
                {template.name}
              </span>
            </div>
            <p className="text-xs text-gray-500 truncate pl-8">
              {template.description}
            </p>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {STRATEGY_TEMPLATES.map((template) => (
        <button
          key={template.id}
          onClick={() => onSelect(template.id, template.path)}
          className={`w-full p-4 rounded-lg border transition-all text-left group ${
            selectedTemplate === template.id
              ? 'border-profit bg-profit/10'
              : 'border-gray-800 bg-gray-800/50 hover:border-gray-700'
          }`}
        >
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className={`p-2.5 rounded-lg flex-shrink-0 ${getIconColor(template.icon)}`}>
              {getIcon(template.icon)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-white">
                  {template.name}
                </span>
                {template.id !== 'blank' && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${getSkillColor(template.skillLevel)}`}>
                    {template.skillLevel}
                  </span>
                )}
                {template.tags?.includes('popular') && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                    Popular
                  </span>
                )}
                {template.tags?.includes('featured') && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">
                    Featured
                  </span>
                )}
                {template.gpuOptimized && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 flex items-center gap-1">
                    <HiSparkles className="w-3 h-3" />
                    GPU
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-400">
                {template.description}
              </p>
            </div>

            {/* Selection indicator */}
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              selectedTemplate === template.id
                ? 'border-profit bg-profit'
                : 'border-gray-600'
            }`}>
              {selectedTemplate === template.id && (
                <FiCheck className="w-3 h-3 text-white" />
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * Encode a notebook path for sessionStorage.
 * If the path matches a directory-based template, returns a JSON string
 * with both path and directoryPath. Otherwise returns the plain path.
 */
export function encodeNotebookPath(path: string): string {
  const template = STRATEGY_TEMPLATES.find(t => t.path === path);
  if (template?.isDirectory && template.directoryPath) {
    return JSON.stringify({ path, directoryPath: template.directoryPath });
  }
  return path;
}
